/**
 * Professional Services documentation categories for the /docs home.
 * Every link points at existing PS content on Confluence or Google Drive —
 * content is linked as a destination, never reproduced in Backstage.
 * Navigation model source: professional_services_documentation_hierarchy.md
 * (PS Confluence landing page + PS Google Drive folder).
 */

export type PsDocSourceType =
  | 'Confluence page'
  | 'Google Doc'
  | 'Drive folder'
  | 'Presentation'
  | 'PDF';

export interface PsDocLink {
  title: string;
  description: string;
  url: string;
  sourceType: PsDocSourceType;
}

export interface PsDocCategory {
  title: string;
  description: string;
  links: PsDocLink[];
}

export const psDocCategories: PsDocCategory[] = [
  {
    title: 'Professional Services Home',
    description:
      'Professional Services landing page and operating framework — the canonical entry points on Confluence.',
    links: [
      {
        title: 'Professional Services',
        description:
          'Primary Professional Services landing page and service catalog.',
        url: 'https://redislabs.atlassian.net/wiki/spaces/PS/pages/1309933868',
        sourceType: 'Confluence page',
      },
      {
        title: 'Professional Services Operating Framework',
        description:
          'Delivery model, process navigation, roles, playbooks, troubleshooting, and lessons learned.',
        url: 'https://redislabs.atlassian.net/wiki/spaces/PS/pages/6306856984',
        sourceType: 'Confluence page',
      },
    ],
  },
  {
    title: 'Training',
    description:
      'Bootcamps, instructor-led administration training, and the Professional Services package catalog.',
    links: [
      {
        title: 'BootCamp Services',
        description:
          'Standard and advanced bootcamp structure, discovery, development, benchmarking, demonstration, and documentation.',
        url: 'https://redislabs.atlassian.net/wiki/spaces/PS/pages/3603071114',
        sourceType: 'Confluence page',
      },
      {
        title: 'Redis Software Administration Training',
        description: 'Instructor-led administration training content.',
        url: 'https://drive.google.com/file/d/1EA4yCg6I71LY6Rxxetvg1T4RY9WY2SKf/view',
        sourceType: 'PDF',
      },
      {
        title: 'Professional Services Package Catalog',
        description:
          'Catalog of Professional Services training and service packages.',
        url: 'https://drive.google.com/file/d/1Kfz3fYg-oDQ-t-_VHfHB8CdRPS-PtIix/view',
        sourceType: 'PDF',
      },
    ],
  },
  {
    title: 'Workshops',
    description:
      'Delivery workshops — Jumpstart, bootcamp, observability, monitoring, cloud operator, and vector search.',
    links: [
      {
        title: 'Jumpstart Playbook',
        description:
          'Jumpstart delivery phases, workshops, readiness assessment, and closure activities.',
        url: 'https://redislabs.atlassian.net/wiki/spaces/PS/pages/6307577869',
        sourceType: 'Confluence page',
      },
      {
        title: 'BootCamp Services',
        description:
          'Use-case discovery, developer workshop, model design, development, benchmarking, and documentation.',
        url: 'https://redislabs.atlassian.net/wiki/spaces/PS/pages/3603071114',
        sourceType: 'Confluence page',
      },
      {
        title: 'Observability Playbook',
        description:
          'Monitoring workshop, observability discovery, dashboards, alerting, validation, and handoff.',
        url: 'https://redislabs.atlassian.net/wiki/spaces/PS/pages/6308691972',
        sourceType: 'Confluence page',
      },
      {
        title: 'Monitoring Workshop',
        description: 'Monitoring workshop materials.',
        url: 'https://drive.google.com/drive/folders/1VYE0c8OkI_JIPcRNMDyShfhYp1WY_Xvx',
        sourceType: 'Drive folder',
      },
      {
        title: 'Cloud Operator Workshop',
        description: 'Cloud operator workshop materials.',
        url: 'https://drive.google.com/drive/folders/1ICpPtJN-mxrBfSSNdi6kwrJInKS_nvWZ',
        sourceType: 'Drive folder',
      },
      {
        title: 'Vector Search Workshop',
        description: 'Vector search workshop presentation.',
        url: 'https://docs.google.com/presentation/d/1cM99QSAavJPtp8lQri6pIoEbIwOI4Oy59Zuz3dmw9YE',
        sourceType: 'Presentation',
      },
    ],
  },
  {
    title: 'How-To',
    description:
      'Delivery processes, playbooks, troubleshooting articles, and technical references.',
    links: [
      {
        title: 'Professional Services Operating Framework',
        description:
          'Navigation to delivery processes, playbooks, and troubleshooting guidance.',
        url: 'https://redislabs.atlassian.net/wiki/spaces/PS/pages/6306856984',
        sourceType: 'Confluence page',
      },
      {
        title: 'Troubleshooting Articles',
        description:
          'Professional Services troubleshooting article collection.',
        url: 'https://redislabs.atlassian.net/wiki/spaces/PS/pages/3338174536',
        sourceType: 'Confluence page',
      },
      {
        title: 'Jumpstart Playbook',
        description: 'Procedural guidance for Jumpstart delivery.',
        url: 'https://redislabs.atlassian.net/wiki/spaces/PS/pages/6307577869',
        sourceType: 'Confluence page',
      },
      {
        title: 'Observability Playbook',
        description:
          'Procedural guidance for monitoring and observability delivery.',
        url: 'https://redislabs.atlassian.net/wiki/spaces/PS/pages/6308691972',
        sourceType: 'Confluence page',
      },
      {
        title: 'Technical Documentation',
        description: 'Technical documentation collection.',
        url: 'https://drive.google.com/drive/folders/1p7AwxN1eJdW864FkGAM9QQ-eH_-pCqBE',
        sourceType: 'Drive folder',
      },
      {
        title: 'Technical Tools',
        description: 'Technical tools and supporting materials.',
        url: 'https://drive.google.com/drive/folders/1AqYaGx0TY0uKEstkHDG5ULV8ThcfAx0-',
        sourceType: 'Drive folder',
      },
      {
        title: 'Monitoring-Offsite',
        description: 'Monitoring user guide and related materials.',
        url: 'https://drive.google.com/drive/folders/1JTDD-fgOnXx7axoFGnbC6UwItnddkHC-',
        sourceType: 'Drive folder',
      },
      {
        title: 'PS Onboarding Source Document',
        description:
          'Procedural onboarding and technical enablement references.',
        url: 'https://docs.google.com/document/d/1q9ifbHuzAxPPjZ5Fbej01HWUOCx5kdotPEOHB9mpn_w',
        sourceType: 'Google Doc',
      },
    ],
  },
  {
    title: 'Onboarding',
    description:
      'Onboarding guides, handbooks, and checklists for new Professional Services team members.',
    links: [
      {
        title: 'Getting Started (New EM Onboarding)',
        description: 'Four-week onboarding guide for new Engagement Managers.',
        url: 'https://redislabs.atlassian.net/wiki/spaces/PS/pages/6329827364',
        sourceType: 'Confluence page',
      },
      {
        title: 'Engagement Managers Handbook',
        description:
          'Engagement Manager reference for the engagement lifecycle and operating model.',
        url: 'https://redislabs.atlassian.net/wiki/spaces/PS/pages/6326157781',
        sourceType: 'Confluence page',
      },
      {
        title: 'Consultants Handbook',
        description:
          'Consulting Engineer reference for technical delivery and internal collaboration.',
        url: 'https://redislabs.atlassian.net/wiki/spaces/PS/pages/6463029253',
        sourceType: 'Confluence page',
      },
      {
        title: 'PS Onboarding Source Document',
        description: 'Professional Services onboarding source and timeline.',
        url: 'https://docs.google.com/document/d/1q9ifbHuzAxPPjZ5Fbej01HWUOCx5kdotPEOHB9mpn_w',
        sourceType: 'Google Doc',
      },
      {
        title: 'New Hire Onboarding Project Instructions',
        description:
          'Instructions for creating an onboarding project from the template.',
        url: 'https://docs.google.com/document/d/1Kc6aoVxOsOR8qdk_gYoKaXsnq2YaNMD0yExdMKdFKgw',
        sourceType: 'Google Doc',
      },
      {
        title: 'Baseline Employee Onboarding Checklist: 0–15 Days',
        description: 'Baseline onboarding checklist.',
        url: 'https://docs.google.com/document/d/1aTqoEGH7QhIe3ycKZ-rbyR-MrbcZiDEXpCo_QYMZym8',
        sourceType: 'Google Doc',
      },
      {
        title: 'Developer Onboarding Checklist: 16–45 Days',
        description: 'Developer-focused onboarding checklist.',
        url: 'https://docs.google.com/document/d/1U60gFIP5_74L-NFr8T82BJwZ3XtV1qqVdOzlvRLEz1k',
        sourceType: 'Google Doc',
      },
      {
        title: 'Operator Onboarding Checklist: 16–45 Days',
        description: 'Operator-focused onboarding checklist.',
        url: 'https://docs.google.com/document/d/1hdEODJBRJ0rffOf_9QhtD2SPvJ_xi2RKgXfreM_nDqs',
        sourceType: 'Google Doc',
      },
      {
        title: 'Persona-Based Onboarding Checklist',
        description:
          'Employee, buddy, and manager onboarding responsibilities.',
        url: 'https://docs.google.com/document/d/1UKjTi5_Azwma8bX68YtfBcGY8YD1wG8WBrU3wlZcClM',
        sourceType: 'Google Doc',
      },
      {
        title: 'First Line Manager Onboarding Checklist',
        description: 'Manager onboarding checklist.',
        url: 'https://docs.google.com/document/d/1sUHTtsJOTz3YcZfIHg8kTCN_Lwv4QjAl3ZeAqmctcQk',
        sourceType: 'Google Doc',
      },
      {
        title: 'Buddy Onboarding Checklist',
        description: 'Buddy onboarding checklist.',
        url: 'https://docs.google.com/document/d/1teic5YjMeYzDLoQKmdvhmYx6ZtHRh0YR1M8VjeCqbTo',
        sourceType: 'Google Doc',
      },
      {
        title: 'Resident Engineer Overview',
        description:
          'Overview of Professional Services training, workshops, and delivery services.',
        url: 'https://drive.google.com/file/d/1D4VXlNAn2ZkxSE-vSMCAolc282eqgD94/view',
        sourceType: 'PDF',
      },
      {
        title: 'Professional Services On-boarding Folder',
        description: 'Onboarding artifact collection.',
        url: 'https://drive.google.com/drive/folders/1f4wcnBKRQdyuUyaXj5TPJVLBW7fTmRs6',
        sourceType: 'Drive folder',
      },
    ],
  },
];
